import React, { useEffect, useMemo, useRef, useState, useContext } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Drawer,
  Switch,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Radio,
  RadioGroup,
  Alert,
  Link,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Upload, UploadFile, Visibility, BorderColorOutlined as Edit, Save, Close, Search, AutoAwesome,
  ContentCopy, Download, Link as LinkIcon, LinkOff, FactCheckOutlined,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import showdown from 'showdown';
import TurndownService from 'turndown';
import DOMPurify from 'isomorphic-dompurify';
import { DataAPIContext } from '../api';
import { ReloadIcon } from '../components/actionIcons';
import DeleteButton from '../components/DeleteButton';
import DeleteIconButton from '../components/DeleteIconButton';
import { CREATE, DELETE, UPDATE, GET_ONE } from '../api/dataProvider';
import WidgetsPicker, { defaultWidgetIdsFor } from './WidgetsPicker';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FormActionButtons from '../components/FormActionButtons';
import QuillEditor from '../components/QuillEditor';
import { ContextStore } from '../store/contextStore';

// Rows added by addLocalFiles carry a client-side placeholder id until the upload's
// CREATE resolves and swaps in the server id. Nothing may be dispatched against such a
// row: the backend has no document for it, so any UPDATE/DELETE 404s (and, before
// 2026-09-11, logged a billed error -- CPBACKEND-EZFJ was exactly this: an operator
// changed the category of a still-uploading doc and the app PUT to
// /ai_knowledge_base_docs/local-1789119598846-0/).
const isTempId = (id) => typeof id === 'string' && (id.startsWith('local-') || id.startsWith('temp-'));

const TAGS_RESOURCE = 'tour_tag';
const TAG_CATEGORY_CODE = 'chat_ai_auto_reply_doc';

// Text-based files are handled in-app: opening the raw S3 URL doesn't work for
// Markdown (browsers won't render text/markdown), and viewing/editing in the
// browser is nicer than download → edit → delete → re-upload. We fetch the
// source and show it in a dialog that can also edit it in place.
const MD_EXTENSIONS = ['.md', '.markdown'];
const TEXT_EXTENSIONS = [...MD_EXTENSIONS, '.txt', '.json', '.csv', '.tsv', '.yaml', '.yml', '.xml', '.log'];
// strip any query string (S3 links) before checking the extension
const extOf = (s) => {
  const path = String(s || '').split('?')[0].toLowerCase();
  const dot = path.lastIndexOf('.');
  return dot >= 0 ? path.slice(dot) : '';
};
const matchesExt = (file, exts) => exts.includes(extOf(file?.name)) || exts.includes(extOf(file?.link));
const isMarkdownFile = (file) => matchesExt(file, MD_EXTENSIONS);
const isTextFile = (file) => matchesExt(file, TEXT_EXTENSIONS);
const isJsonFile = (file) => extOf(file?.name) === '.json' || extOf(file?.link) === '.json';
// Auto-generated docs written nightly by the backend curator: the policy-lessons doc
// "<Operator>_AI_Curated_Lessons.md" and the phrasing/style doc "<Operator>_AI_Response_Style.md"
// feed the AI once a human activates them, so they share the "Auto-generated" pill.
// The staff-assertions worksheet "<Operator>_Staff_Assertions_Backlog.md" (the values staff typed
// into corrections) is different: the AI never reads it, active or not, so it gets its own
// "For review only" pill, no Active switch, and sorts below the real training docs -- the shared
// pill's "not used until you switch it on" invited operators to activate it and expect an effect.
// Keep in sync with curate_ai_corrections (_DOC_SUFFIX /
// AI_Knowledge_Base_Document.RESPONSE_STYLE_SUFFIX / STAFF_ASSERTIONS_SUFFIX).
const AUTO_LESSONS_SUFFIX = '_AI_Curated_Lessons.md';
const RESPONSE_STYLE_SUFFIX = '_AI_Response_Style.md';
const STAFF_ASSERTIONS_SUFFIX = '_Staff_Assertions_Backlog.md';
const nameEndsWith = (name, suffix) => (name || '').toLowerCase().endsWith(suffix.toLowerCase());
const isAutoLessonsDoc = (name) => [AUTO_LESSONS_SUFFIX, RESPONSE_STYLE_SUFFIX].some((s) => nameEndsWith(name, s));
const isStaffAssertionsDoc = (name) => nameEndsWith(name, STAFF_ASSERTIONS_SUFFIX);
const STAFF_ASSERTIONS_EXPLAINER = 'A list of facts your team has typed into replies to guests, collected nightly. '
  + 'The AI never reads this file, whether or not it is active. Copy the facts that are correct into a '
  + 'regular knowledge base document so the AI can use them.';

const mdConverter = new showdown.Converter({
  tables: true,
  simplifiedAutoLink: true,
  strikethrough: true,
  tasklists: true,
  openLinksInNewWindow: true,
  // Underscores inside a word stay underscores: the AI Curated Lessons headings are topic keys
  // ("## booking_confirm_details"), which showdown otherwise renders as "booking<em>confirm</em>details".
  literalMidWordUnderscores: true,
});
export const renderMarkdown = (md) => DOMPurify.sanitize(mdConverter.makeHtml(md || ''), { ADD_ATTR: ['target'] });

// Word -> Markdown editing is implemented but switched OFF (2026-08-13): the
// feature works, the workflow decision is pending. While this is false, Word
// documents open the stored file exactly as they did before -- and the backend
// refuses the conversion independently (WORD_CONVERSION_ENABLED in
// AI_Knowledge_Base_DocumentViewset), so both sides have to be flipped to
// re-enable. Unaffected: "Replace file", and the formatted editor for Markdown.
const WORD_EDITING_ENABLED = false;

// Word documents we can convert to editable Markdown in the browser. Mirrors
// AI_Knowledge_Base_DocumentViewset.DOC_CONVERT_SUFFIXES. Legacy binary .doc is
// excluded on purpose -- neither mammoth nor the backend extractor can read it,
// so those go through "Replace file" instead.
const WORD_EXTENSIONS = ['.docx'];
const isWordFile = (file) => WORD_EDITING_ENABLED && matchesExt(file, WORD_EXTENSIONS);

/* ===================== Markdown <-> HTML ===================== */

// Markdown is the single source of truth while a document is open: the
// formatted editor is a *view* over it (markdown -> showdown -> HTML -> Quill,
// and back through turndown on change). One canonical form means the source
// toggle, the rendered preview and the save payload can never disagree.
const turndownService = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '-',
  codeBlockStyle: 'fenced',
  emDelimiter: '*',
});

// Turndown ships no table rule -- without this a Word table would arrive as a
// run of loose text the moment the document is opened, losing its structure
// before the operator touches anything. Emit GFM tables instead, which showdown
// renders back (tables: true above) and the source view can edit by hand.
const tableCellText = (cell) => (cell.textContent || '').replace(/\s+/g, ' ').replace(/\|/g, '\\|').trim();
turndownService.addRule('gfmTable', {
  filter: 'table',
  replacement: (content, node) => {
    const rows = Array.from(node.querySelectorAll('tr'));
    if (!rows.length) return content;
    const grid = rows.map((tr) => Array.from(tr.querySelectorAll('th,td')).map(tableCellText));
    const width = Math.max(...grid.map((r) => r.length));
    const padded = grid.map((r) => [...r, ...Array(width - r.length).fill('')]);
    const [head, ...body] = padded;
    const line = (cells) => `| ${cells.join(' | ')} |`;
    return `\n\n${[line(head), line(head.map(() => '---')), ...body.map(line)].join('\n')}\n\n`;
  },
});

const htmlToMarkdown = (html) => turndownService.turndown(html || '');

// Word embeds images as binary parts and mammoth inlines them as base64 data
// URIs. Left alone they would bloat the stored Markdown and fill the vector
// index with megabytes of meaningless characters -- the KB is text-only. Strip
// them and report the count so the operator knows what was dropped.
const stripImages = (html) => {
  const matches = html.match(/<img\b[^>]*>/gi) || [];
  return { html: html.replace(/<img\b[^>]*>/gi, ''), removed: matches.length };
};

// Quill has no table support: `formats` omits it, so its clipboard strips
// <table> on the way in and the rows collapse into loose text. Documents with
// tables therefore open in source view with the formatted editor disabled.
const containsTable = (html) => /<table\b/i.test(html || '');
const TABLE_WARNING =
  'This document contains a table. Tables are kept as text, but the formatted editor cannot edit them, '
  + 'so this document opens in source view.';

// Toolbar for knowledge-base prose. Wider than the notification-template
// default (which is tuned for short SMS/email bodies) but still limited to
// what turndown can round-trip into Markdown without loss.
const KB_QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'code-block'],
    ['link'],
    ['clean'],
  ],
  clipboard: { matchVisual: false },
};
const KB_QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent', 'blockquote', 'code-block', 'link',
];

const getStatusChip = (status) => {
  const colorMap = {
    Processing: 'warning',
    Completed: 'success',
    Failed: 'error',
  };
  return <Chip label={status} color={colorMap[status]} variant="outlined" size="small"/>;
};

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}
function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    return order !== 0 ? order : a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}

/* ===================== Tag helpers ===================== */

// Convert any incoming shape to a normalized { id, name } object
const normalizeTag = (t) => {
  if (!t) return { id: null, name: '' };
  if (typeof t === 'string') return { id: null, name: t };
  if (typeof t === 'object') {
    const id = t.id ?? t._id ?? t.value ?? t?.data?.id ?? null;
    const name = t.name ?? t.label ?? t?.data?.name ?? '';
    return { id, name };
  }
  return { id: null, name: String(t) };
};

// Extract display name from any shape
const tagToName = (t) => normalizeTag(t).name;

// Extract id from any shape (null if none)
const tagToId = (t) => normalizeTag(t).id;

// Compare two tag-like values (prefer id, else name)
const tagsEqual = (a, b) => {
  const aid = tagToId(a); const bid = tagToId(b);
  if (aid && bid) return aid === bid;
  return tagToName(a).toLowerCase() === tagToName(b).toLowerCase();
};

// Render options with matching substring bolded
const renderBoldTagOption = (props, option, { inputValue }) => {
  const label = tagToName(option);
  if (!inputValue) {
    return (
      <Box component="li" {...props}>
        {label}
      </Box>
    );
  }
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(esc(inputValue), 'ig');
  const html = label.replace(re, (m) => `<b style="color:black !important;">${m}</b>`);
  return (
    <Box component="li" {...props}>
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </Box>
  );
};

// KB channels (mirror AI_Knowledge_Base_Document.channel on the backend). The web
// widget and the support auto/suggested-reply bot have fully separate knowledge bases.
// 'both' is a SHARED document: one row that appears in, and feeds, both knowledge
// bases -- editing it from either screen updates the other (unlike copy_to_channel,
// which forks an independent copy).
const KB_CHANNEL_SUPPORT = 'support';
const KB_CHANNEL_WIDGET = 'widget';
const KB_CHANNEL_BOTH = 'both';
const KB_CHANNEL_LABELS = { [KB_CHANNEL_SUPPORT]: 'Support KB', [KB_CHANNEL_WIDGET]: 'Web Widget KB' };
const isSharedDoc = (file) => file?.channel === KB_CHANNEL_BOTH;

// In-app text viewer/editor dialog for text-based docs (md/txt/json/...).
// The raw S3 URL isn't browser-viewable for Markdown, and editing here avoids
// the download → edit → delete → re-upload round-trip.
const EMPTY_PREVIEW = {
  open: false, file: null, name: '', isMarkdown: false, editable: false,
  content: '', loading: false, error: '', editing: false, draft: '', saving: false, showRendered: true,
  // Formatted (WYSIWYG) editing. `rich` gates the toggle, `wysiwyg` is the
  // current mode, and `quillHtml` is the HTML handed to Quill when editing
  // starts -- held fixed for the session so the markdown -> HTML -> markdown
  // round trip can't fight the editor's own value-sync effect.
  rich: false, wysiwyg: false, quillHtml: '', warning: '',
  // Set for Word documents: saving rewrites the .docx as .md (backend
  // `convert_to`), which needs the operator's decision on the original file.
  convertTo: null,
};

const MAX_SIZE_MB = 50;
const ACCEPTED_EXT = ['.pdf', '.doc', '.docx', '.txt', '.md', '.json', '.csv', '.xls', '.xlsx'];

/**
 * Self-contained knowledge-base document manager for a single fixed KB channel.
 * Renders the upload/search/table panel, the edit drawer, the delete confirmation,
 * and the in-app text preview/editor dialog.
 */
export default function KnowledgeBaseManager({ channel, effectiveTopId, hasWrite, hasDelete, webWidgetAvailable, onDocCountChange, onOpenTrainingInbox, widgets = [], widget = null }) {
  const { enqueueSnackbar } = useSnackbar();
  // CP-8335: an operator with several branded web widgets sees the documents of the widget
  // picked on the page, and assigns documents to widgets in the edit drawer.
  const multiWidget = channel !== KB_CHANNEL_SUPPORT && widgets.length > 1 && !!widget;
  const widgetId = multiWidget ? widget.id : null;
  const dataAPI = useContext(DataAPIContext);
  const { hasPerm } = useContext(ContextStore);
  // Trained answers reach the chatbot through a generated knowledge-base doc that the backend
  // keeps out of this list (it's managed only in the AI training inbox), so say where they
  // are. The host page passes `onOpenTrainingInbox` (switch to its inbox tab) only where
  // that tab is shown; a plain link can't switch AI Suite's tabs, which read the URL on load.
  const showTrainingInboxNote = !!onOpenTrainingInbox;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const resource = 'tour_operator/ai_knowledge_base_docs';
  // CP-8187: copying INTO the widget KB writes a widget document, which the backend
  // gates on web_widget.write regardless of which channel's screen you started from.
  // Sharing/unsharing goes through the same gate: either direction changes what the
  // widget KB contains.
  const canCopyToOtherChannel =
    hasWrite && (channel === KB_CHANNEL_WIDGET || hasPerm('web_widget', 'write'));
  const canShareAcrossChannels = canCopyToOtherChannel;
  // A shared document also lives in the widget KB, so mutating it from the support
  // screen needs the widget-side permission the backend enforces (CP-8187).
  const fileHasWrite = (file) =>
    hasWrite && (!isSharedDoc(file) || channel === KB_CHANNEL_WIDGET || hasPerm('web_widget', 'write'));
  const fileHasDelete = (file) =>
    hasDelete && (!isSharedDoc(file) || channel === KB_CHANNEL_WIDGET || hasPerm('web_widget', 'delete'));

  const [files, setFiles] = useState([]);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true); // only for initial fetch, avoids flicker on polls
  const [, setRefreshing] = useState(false); // light refresh state (no blank table)
  const [selection, setSelection] = useState({});
  const [uploading, setUploading] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null); // file to delete (for confirmation dialog)

  const [editingTagsId, setEditingTagsId] = useState(null);
  const [tagDrafts, setTagDrafts] = useState({}); // id -> normalized tag[]

  const [editingKBId, setEditingKBId] = useState(null);
  const [kbDrafts, setKbDrafts] = useState({}); // id -> string

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingFile, setEditingFile] = useState(null); // holds normalized tags in .tags
  // CP-8441: the Edit File drawer's SAVE waits until a field differs from the file it opened with.
  const [editingFileBaseline, setEditingFileBaseline] = useState(null);
  const editFileSnapshot = (f) => JSON.stringify(f ? {
    name: f.name || '',
    is_active: f.is_active,
    category: f.category || 'Uncategorized',
    tags: (f.tags || []).map(normalizeTag).map((t) => `${t.id ?? ''}:${t.name ?? ''}`),
    widgets: (f.widgets || []).map((w) => w.id),
  } : null);
  const editingFileChanged = !!editingFile && editFileSnapshot(editingFile) !== editingFileBaseline;

  // Query text and options for RemoteAutocomplete instances
  const [tagQueryByFile, setTagQueryByFile] = useState({});    // fileId -> query string
  const [optionsByFile, setOptionsByFile] = useState({});       // fileId -> options[]
  const [drawerTagQuery, setDrawerTagQuery] = useState('');     // drawer query
  const [drawerTagOptions, setDrawerTagOptions] = useState([]); // drawer options[]

  const [preview, setPreview] = useState(EMPTY_PREVIEW);
  const [convertPrompt, setConvertPrompt] = useState({ open: false, keepOriginal: true });
  const [fileToReplace, setFileToReplace] = useState(null);          // row awaiting a replacement file
  const [pendingReplacement, setPendingReplacement] = useState(null); // { file, incoming } awaiting confirmation

  const fileInputRef = useRef();
  const replaceInputRef = useRef();
  const dropRef = useRef();
  const pollingRef = useRef(null);
  // Save reads the draft through a ref: the formatted editor's onChange is
  // debounced, so the value in scope when the click handler was created can lag
  // behind what the operator actually typed.
  const draftRef = useRef('');
  useEffect(() => { draftRef.current = preview.draft; }, [preview.draft]);

  const handleFileUploadClick = () => fileInputRef.current?.click();

  const mapServerItem = (item) => ({
    id: item.id ?? item._id ?? item.name,
    name: item.name,
    is_active: item.is_active ? 'Yes' : 'No',
    status: item.status || 'Completed',
    category: item.category || 'Uncategorized',
    channel: item.channel || KB_CHANNEL_SUPPORT,
    // Keep tags as normalized objects {id,name} for UI
    tags: (item.tags || []).map(normalizeTag),
    widgets: item.widgets || [],
    link: item.link,
    created_ts_display: item.created_ts
      ? new Date(item.created_ts).toLocaleString('en-US', {
          month: 'short',
          day: '2-digit',
          year: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZoneName: 'short',
        })
      : '',
    createdAt: item.created_ts ? new Date(item.created_ts).getTime() : Date.now(),
  });

  const mergeById = (prev, incoming) => {
    const incomingByName = new Map(
      incoming
        .filter(i => i?.name)
        .map(i => [i.name.trim().toLowerCase(), i])
    );

    // Drop temp locals if server version detected
    const keep = [];
    for (const p of prev) {
      const isTemp = isTempId(p.id);
      if (isTemp) {
        const key = p.name?.trim().toLowerCase();
        const nameMatch = key && incomingByName.has(key);
        const linkMatch = p.link && incoming.some(i => i.link && i.link === p.link);
        const timeMatch = incoming.some(i =>
          Math.abs((i.createdAt || 0) - (p.createdAt || 0)) <= 5 * 60 * 1000 &&
          i.name?.trim().toLowerCase() === key
        );
        if (nameMatch || linkMatch || timeMatch) continue;
      }
      keep.push(p);
    }

    const byId = new Map(keep.map(k => [k.id, k]));

    for (const inc of incoming) {
      const existing = byId.get(inc.id);
      if (existing) {
        byId.set(inc.id, { ...existing, ...inc });
        continue;
      }
      const key = inc.name?.trim().toLowerCase();
      if (key) {
        const tempEntry = Array.from(byId.values()).find(v =>
          isTempId(v.id) &&
          v.name?.trim().toLowerCase() === key
        );
        if (tempEntry) {
          byId.delete(tempEntry.id);
          byId.set(
            inc.id,
            {
              ...tempEntry,
              ...inc,
              id: inc.id,
              // prefer server tags if present; otherwise keep local (filter out "Uploaded")
              tags: inc.tags ? inc.tags.map(normalizeTag) : tempEntry.tags?.filter(t => tagToName(t) !== 'Uploaded')?.map(normalizeTag) || []
            }
          );
          continue;
        }
      }
      byId.set(inc.id, inc);
    }

    return Array.from(byId.values());
  };

  const isEditing = Boolean(editingTagsId || editingKBId);

  const fetchFiles = async ({ showSpinner } = { showSpinner: false }) => {
    if (isEditing) return; // pause polling during edits
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await dataAPI('GET_LIST', resource, {
        channel,
        ...(effectiveTopId ? { franchise_to: effectiveTopId } : {}),
        ...(widgetId ? { widget: widgetId } : {}),
      });
      if (res && Array.isArray(res)) {
        const incoming = res.map(mapServerItem);
        setFiles((prev) => mergeById(prev, incoming));
        onDocCountChange?.(incoming.length);
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch training files', { variant: 'error' });
    } finally {
      if (showSpinner) setLoading(false);
      else setRefreshing(false);
    }
  };

  useEffect(() => {
    // Clear rows when the operator or channel changes so the previous channel's
    // docs don't linger (mergeById keeps non-temp prev rows across fetches).
    setFiles([]);
    // initial
    fetchFiles({ showSpinner: true });
    // polling without spinner to prevent table blanking
    pollingRef.current = setInterval(() => {
      fetchFiles({ showSpinner: false });
    }, 10000);
    return () => clearInterval(pollingRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveTopId, channel, widgetId]);

  const kbOptions = useMemo(() => {
    const defaults = ['FAQ Support', 'Customer Support', 'Tour info', 'Product manual', 'Uncategorized'];
    const set = new Set(defaults);
    for (const f of files) if (f.category) set.add(f.category);
    return Array.from(set);
  }, [files]);

  const filteredFiles = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return files;
    return files.filter((f) => [f.name, f.category, ...(f.tags || []).map(tagToName)]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(q));
  }, [files, filter]);

  const closePreview = () => setPreview(EMPTY_PREVIEW);

  const openTextPreview = async (file) => {
    const isMarkdown = isMarkdownFile(file);
    const base = { ...EMPTY_PREVIEW, open: true, file, name: file.name, isMarkdown, showRendered: isMarkdown };
    // Just-uploaded local files have the bytes in hand; no need to round-trip.
    if (file.file) {
      try {
        const text = await file.file.text();
        // Local-only files aren't persisted yet, so don't offer editing until uploaded.
        setPreview({ ...base, content: text, draft: text, editable: false });
      } catch (e) {
        setPreview({ ...base, error: 'Could not read this file' });
      }
      return;
    }
    setPreview({ ...base, loading: true });
    try {
      const res = await dataAPI(GET_ONE, resource, { id: `${file.id}/content` });
      const text = res?.data?.content ?? '';
      const editable = !!res?.data?.editable && fileHasWrite(file);
      const tables = isMarkdown && containsTable(renderMarkdown(text));
      setPreview({
        ...base,
        content: text,
        draft: text,
        editable,
        loading: false,
        // Prose gets the formatted editor; JSON/CSV/YAML/logs stay in the plain
        // text editor, where structure matters more than formatting and a
        // markdown round trip would corrupt the file.
        rich: editable && isMarkdown && !tables,
        warning: tables ? TABLE_WARNING : '',
      });
    } catch (e) {
      setPreview({ ...base, loading: false, error: 'Could not load this document' });
    }
  };

  // Word documents are converted to Markdown in the browser (mammoth), which is
  // why this needs the raw bytes rather than the server's flat text extraction:
  // mammoth keeps headings, lists, links and tables that the extractor drops.
  // Nothing is written until the operator saves.
  const openWordPreview = async (file) => {
    const base = {
      ...EMPTY_PREVIEW, open: true, file, name: file.name, isMarkdown: true, showRendered: true, convertTo: 'md',
    };
    setPreview({ ...base, loading: true });
    try {
      const res = await dataAPI(GET_ONE, resource, { id: file.id, urlSuffix: 'raw/' });
      // Distinguish "the server didn't give us the file" from "the file isn't
      // readable": both used to surface as one opaque message, and the first
      // one is usually a backend that predates the /raw/ endpoint.
      const status = res?.response?.status ?? res?.statusCode;
      if (!res?.response || (status && status >= 400)) {
        throw new Error(`server returned HTTP ${status || 'no response'} for the document file`);
      }
      const arrayBuffer = await res.response.arrayBuffer();
      // A .docx is a ZIP: anything else here is a mislabelled file (most often
      // a legacy binary .doc renamed) or an error page that arrived with a 200.
      const magic = new Uint8Array(arrayBuffer.slice(0, 4));
      if (!(magic[0] === 0x50 && magic[1] === 0x4b && magic[2] === 0x03 && magic[3] === 0x04)) {
        throw new Error('not a .docx — the file is not a Word (Open XML) document');
      }
      // Loaded on demand: mammoth pulls in jszip and is far too big to sit in
      // the main bundle for a feature most sessions never open.
      const mammoth = await import('mammoth');
      const { value: rawHtml } = await mammoth.convertToHtml({ arrayBuffer });
      const { html, removed } = stripImages(rawHtml);
      const tables = containsTable(html);
      const markdown = htmlToMarkdown(html);
      const warnings = [
        tables ? TABLE_WARNING : '',
        removed ? `${removed} image${removed === 1 ? '' : 's'} were removed — the knowledge base indexes text only.` : '',
      ].filter(Boolean);
      setPreview({
        ...base,
        content: markdown,
        draft: markdown,
        editable: fileHasWrite(file),
        rich: fileHasWrite(file) && !tables,
        warning: warnings.join(' '),
        loading: false,
      });
    } catch (e) {
      // Surface the reason: without it every failure mode (missing endpoint,
      // S3 read error, mislabelled file, unreadable .docx) looks identical.
      console.error('KB: could not open Word document', file?.name, e);
      setPreview({
        ...base,
        loading: false,
        error: `Could not read this Word document — ${e?.message || 'unknown error'}. `
          + 'You can still download it from the document list and use Replace file to upload an edited version.',
      });
    }
  };

  const startEditPreview = () => setPreview((p) => ({
    ...p, editing: true, draft: p.content, showRendered: false,
    wysiwyg: p.rich, quillHtml: p.rich ? renderMarkdown(p.content) : '',
  }));
  const cancelEditPreview = () => setPreview((p) => ({
    ...p, editing: false, draft: p.content, showRendered: p.isMarkdown, wysiwyg: false, quillHtml: '',
  }));

  // Switching modes re-derives the target view from the markdown draft, which
  // both directions keep current (Quill's onChange writes back through turndown).
  const toggleWysiwyg = () => setPreview((p) => ({
    ...p, wysiwyg: !p.wysiwyg, quillHtml: !p.wysiwyg ? renderMarkdown(p.draft) : '',
  }));

  const savePreview = async ({ keepOriginal } = {}) => {
    const file = preview.file;
    if (!file) return;
    // The formatted editor debounces its onChange by 300ms; let it settle so
    // keystrokes just before the click aren't dropped from the payload.
    if (preview.wysiwyg) await new Promise((resolve) => setTimeout(resolve, 350));
    const draft = draftRef.current;
    if (isJsonFile(file)) {
      try {
        JSON.parse(draft);
      } catch (e) {
        enqueueSnackbar('Invalid JSON — please fix before saving', { variant: 'warning' });
        return;
      }
    }
    if (!assertPersisted(file, 'edit its content')) return;
    setPreview((p) => ({ ...p, saving: true }));
    try {
      const payload = { content: draft };
      if (preview.convertTo) {
        payload.convert_to = preview.convertTo;
        payload.keep_original = !!keepOriginal;
      }
      const res = await dataAPI(UPDATE, resource, { id: `${file.id}/content`, data: payload });
      if (preview.convertTo) {
        // The row was renamed to .md and may have gained an archive row, so the
        // local list can't be patched in place — refetch and close.
        enqueueSnackbar(
          `Converted to ${res?.data?.name || 'Markdown'}${keepOriginal ? ' — original kept as an inactive archive' : ''}`,
          { variant: 'success' },
        );
        closePreview();
        fetchFiles();
        return;
      }
      setPreview((p) => ({ ...p, saving: false, editing: false, content: draft, draft, showRendered: p.isMarkdown, wysiwyg: false, quillHtml: '' }));
      // Re-indexing was triggered server-side; reflect the Processing status.
      setFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, status: 'Processing' } : f)));
      enqueueSnackbar('Document saved — re-processing knowledge base', { variant: 'success' });
    } catch (e) {
      setPreview((p) => ({ ...p, saving: false }));
      enqueueSnackbar('Failed to save document', { variant: 'error' });
    }
  };

  // Word docs can't be saved in place, so the first save asks what to do with
  // the original before anything is written.
  const requestSave = () => {
    if (preview.convertTo) {
      setConvertPrompt({ open: true, keepOriginal: true });
      return;
    }
    savePreview();
  };

  const confirmConvert = () => {
    const { keepOriginal } = convertPrompt;
    setConvertPrompt({ open: false, keepOriginal: true });
    savePreview({ keepOriginal });
  };

  const handlePreview = (file) => {
    if (isWordFile(file) && !file.file) {
      openWordPreview(file);
    } else if (isTextFile(file)) {
      openTextPreview(file);
    } else if (file.file) {
      const url = URL.createObjectURL(file.file);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } else if (file.link) {
      window.open(file.link, '_blank', 'noopener,noreferrer');
    } else {
      enqueueSnackbar('Preview not available for this file', { variant: 'info' });
    }
  };

  const handleDelete = (file) => {
    setFileToDelete(file);
  };

  // CP-7771: the backend's delete now runs (it never did): it rebuilds the knowledge base and refuses a widget or
  // shared document without web_widget.delete (CP-8187) with a 403. keepSessionOn403 makes that refusal an answer
  // shown here, not a sign-out. 404 = already gone. Resolves to null when deleted, else the reason.
  const deleteServerDoc = async (id) => {
    const res = await dataAPI(DELETE, resource, { id, keepSessionOn403: true });
    if (res?.statusCode === 204 || res?.statusCode === 404) return null;
    return res?.data?.message || res?.data?.detail || 'Failed to delete file';
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;
    const file = fileToDelete;
    setFileToDelete(null);
    try {
      // A still-uploading row has no server document: just drop it locally.
      if (file.id && !isTempId(file.id) && typeof dataAPI === 'function') {
        const refused = await deleteServerDoc(file.id);
        if (refused) {
          enqueueSnackbar(refused, { variant: 'error' });
          return;
        }
      }
      setFiles((prev) => prev.filter((f) => f.id !== file.id));
      enqueueSnackbar('File deleted', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('Failed to delete file', { variant: 'error' });
    }
  };

  const cancelDelete = () => {
    setFileToDelete(null);
  };

  const handleBulkDelete = async () => {
    const toDelete = Object.entries(selection).filter(([, v]) => v).map(([id]) => id);
    if (!toDelete.length) return;
    // Still-uploading rows have no server document; remove them locally only. One request per file: the backend
    // debounces the knowledge-base rebuild, so a burst of deletes rebuilds once.
    const refusals = await Promise.all(toDelete.map((id) => (isTempId(id)
      ? Promise.resolve(null)
      : deleteServerDoc(id).catch(() => 'Failed to delete file'))));
    const kept = new Set(toDelete.filter((id, i) => refusals[i]));
    // Selection keys are strings; ids may be numbers.
    setFiles((prev) => prev.filter((f) => !toDelete.includes(String(f.id)) || kept.has(String(f.id))));
    setSelection(Object.fromEntries([...kept].map((id) => [id, true])));
    if (!kept.size) {
      enqueueSnackbar('Selected files deleted', { variant: 'success' });
    } else {
      const reason = refusals.find(Boolean);
      enqueueSnackbar(`${kept.size} of ${toDelete.length} files were not deleted: ${reason}`, { variant: 'error', autoHideDuration: 8000 });
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const validateFiles = (fileList) => {
    const filesArr = Array.from(fileList);
    const oversized = filesArr.find((f) => f.size > MAX_SIZE_MB * 1024 * 1024);
    if (oversized) { enqueueSnackbar(`${oversized.name} exceeds ${MAX_SIZE_MB}MB limit`, { variant: 'warning' }); return []; }
    const badType = filesArr.find((f) => !ACCEPTED_EXT.some((ext) => f.name.toLowerCase().endsWith(ext)));
    if (badType) { enqueueSnackbar(`${badType.name} is not an accepted type`, { variant: 'warning' }); return []; }
    return filesArr;
  };

  // Every per-row action funnels through this before touching the API. A row whose upload
  // has not finished has no server document yet, so the only honest answer is "wait".
  const assertPersisted = (file, verb) => {
    if (!isTempId(file?.id)) return true;
    enqueueSnackbar(`“${file?.name || 'This file'}” is still uploading — wait a moment before you ${verb}`, { variant: 'info' });
    return false;
  };

  const addLocalFiles = (uploadedFiles) => {
    const now = Date.now();
    const newFiles = uploadedFiles.map((file, idx) => ({
      id: `local-${now}-${idx}`,
      name: file.name,
      is_active: 'No',
      status: 'Processing',
      category: 'Uncategorized',
      tags: [normalizeTag('Uploaded')],
      created_ts_display: new Date().toLocaleString('en-US', { month: 'short', day: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false, timeZoneName: 'short' }),
      createdAt: now,
      file,
    }));
    setFiles((prev) => [...newFiles, ...prev]);
    return newFiles;
  };

  const uploadFiles = async (filesToUpload) => {
    setUploading(true);
    try {
      for (const f of filesToUpload) {
        try {
          const newWidgetIds = multiWidget ? defaultWidgetIdsFor(widgets, widget) : [];
          const res = await dataAPI(CREATE, resource, {
            data: {
              name: f.name,
              attachments: [f.file],
              channel,
              ...(effectiveTopId ? { tour_operator: effectiveTopId } : {}),
              ...(newWidgetIds.length ? { widget_ids: newWidgetIds } : {}),
            },
            upload: true,
          });
          if (res?.statusCode === 200 || res?.id) {
            enqueueSnackbar(`${f.name} uploaded`, { variant: 'success' });
            setFiles((prev) => prev.map((p) => p.id === f.id ? {
              ...p,
              id: res.id ?? p.id,
              is_active: res.is_active ? 'Yes' : 'No',
              status: res.status || 'Processing',
              category: res.category || p.category,
              tags: (res.tags || p.tags || []).map(normalizeTag),
              link: res.link || p.link,
              created_ts_display: res.created_ts ? new Date(res.created_ts).toLocaleString('en-US', { month: 'short', day: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false, timeZoneName: 'short' }) : p.created_ts_display,
              createdAt: res.created_ts ? new Date(res.created_ts).getTime() : p.createdAt,
              file: undefined,
            } : p));
          } else if (res?.statusCode === 406) {
            enqueueSnackbar(`${f.name} failed validation`, { variant: 'warning' });
            setFiles((prev) => prev.map((p) => p.id === f.id ? { ...p, status: 'Failed' } : p));
          }
        } catch (err) {
          enqueueSnackbar(`Error uploading ${f.name}`, { variant: 'error' });
          setFiles((prev) => prev.map((p) => p.id === f.id ? { ...p, status: 'Failed' } : p));
        }
      }
    } finally {
      setUploading(false);
    }
  };

  // Fork a document into the OTHER channel's KB. This is a copy, not a link -- the
  // new doc is independent and later edits to either never propagate (backend
  // copy_to_channel action). The copy lands in the other channel, so it won't appear
  // in the current view; the snackbar confirms it.
  const handleCopyToOtherChannel = async (file) => {
    const target = channel === KB_CHANNEL_SUPPORT ? KB_CHANNEL_WIDGET : KB_CHANNEL_SUPPORT;
    try {
      await dataAPI(CREATE, resource, {
        urlSuffix: `${file.id}/copy_to_channel/`,
        data: { channel: target },
      });
      enqueueSnackbar(`Copied “${file.name}” to ${KB_CHANNEL_LABELS[target]}`, { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(`Failed to copy “${file.name}” to ${KB_CHANNEL_LABELS[target]}`, { variant: 'error' });
    }
  };

  // Share/unshare a document across the two KBs. Sharing sets channel to 'both':
  // the SAME row is then listed in both screens and indexed into both KBs, so an
  // edit made in either place updates the other. Unsharing keeps the document in
  // the KB you're looking at only (channel = this screen's channel) -- the other
  // KB simply stops including it; nothing is deleted.
  const otherChannelLabel = channel === KB_CHANNEL_SUPPORT
    ? KB_CHANNEL_LABELS[KB_CHANNEL_WIDGET]
    : KB_CHANNEL_LABELS[KB_CHANNEL_SUPPORT];
  const handleShareToggle = async (file) => {
    if (!assertPersisted(file, 'share it')) return;
    const sharing = !isSharedDoc(file);
    const target = sharing ? KB_CHANNEL_BOTH : channel;
    try {
      await dataAPI(UPDATE, resource, { id: file.id, data: { channel: target } });
      // The backend re-indexes the affected KB(s); reflect it until the next poll.
      setFiles((prev) => prev.map((f) => (
        f.id === file.id ? { ...f, channel: target, status: 'Processing' } : f
      )));
      enqueueSnackbar(
        sharing
          ? `“${file.name}” is now shared with ${otherChannelLabel} — edits from either screen update both`
          : `“${file.name}” is no longer shared — it now lives only in ${KB_CHANNEL_LABELS[channel]}`,
        { variant: 'success' },
      );
    } catch (err) {
      enqueueSnackbar(`Failed to ${sharing ? 'share' : 'unshare'} “${file.name}”`, { variant: 'error' });
    }
  };

  // "Delete" on a shared document, scoped to this screen: unshare it the other
  // way round, so it lives on only in the OTHER KB. Deleting the row would take
  // it out of both, which is rarely what someone deleting from one screen means.
  const otherChannel = channel === KB_CHANNEL_SUPPORT ? KB_CHANNEL_WIDGET : KB_CHANNEL_SUPPORT;
  const removeFromThisChannelOnly = async () => {
    if (!fileToDelete) return;
    const file = fileToDelete;
    setFileToDelete(null);
    if (!assertPersisted(file, 'remove it')) return;
    try {
      await dataAPI(UPDATE, resource, { id: file.id, data: { channel: otherChannel } });
      // No longer part of this screen's KB, so it leaves this list.
      setFiles((prev) => prev.filter((f) => f.id !== file.id));
      enqueueSnackbar(
        `“${file.name}” removed from ${KB_CHANNEL_LABELS[channel]} — it is still in ${otherChannelLabel}`,
        { variant: 'success' },
      );
    } catch (err) {
      enqueueSnackbar(`Failed to remove “${file.name}” from ${KB_CHANNEL_LABELS[channel]}`, { variant: 'error' });
    }
  };

  const onFileInputChange = async (event) => {
    const validated = validateFiles(event.target.files);
    if (!validated.length) { event.target.value = ''; return; }
    const staged = addLocalFiles(validated);
    event.target.value = '';
    await uploadFiles(staged);
  };

  /* ===================== Replace file (new version) ===================== */

  // Swap the bytes behind an existing document instead of delete-and-re-upload:
  // the row keeps its id, tags, category and active flag, and the backend
  // re-derives the hash/content type and re-indexes.
  const handleReplaceClick = (file) => {
    setFileToReplace(file);
    replaceInputRef.current?.click();
  };

  const onReplaceInputChange = async (event) => {
    const file = fileToReplace;
    const validated = validateFiles(event.target.files);
    event.target.value = '';
    setFileToReplace(null);
    if (!file || !validated.length) return;
    const incoming = validated[0];
    // A different extension changes how the backend extracts text, so the
    // stored name has to follow the file rather than silently disagree with it.
    if (extOf(incoming.name) !== extOf(file.name)) {
      setPendingReplacement({ file, incoming });
      return;
    }
    await replaceFile(file, incoming, false);
  };

  const replaceFile = async (file, incoming, rename) => {
    if (!assertPersisted(file, 'replace it')) return;
    const name = rename ? incoming.name : file.name;
    setFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, status: 'Processing' } : f)));
    try {
      await dataAPI(UPDATE, resource, { id: file.id, data: { name, attachments: [incoming] }, upload: true });
      setFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, name, status: 'Processing' } : f)));
      enqueueSnackbar(`“${file.name}” replaced — re-processing knowledge base`, { variant: 'success' });
    } catch (e) {
      setFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, status: 'Failed' } : f)));
      enqueueSnackbar(`Failed to replace “${file.name}”`, { variant: 'error' });
    }
  };

  const confirmReplacement = async () => {
    const pending = pendingReplacement;
    setPendingReplacement(null);
    if (pending) await replaceFile(pending.file, pending.incoming, true);
  };

  // Drag & drop
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    if (!hasWrite) return; // read-only: no drag and drop handlers
    const prevent = (e) => { e.preventDefault(); e.stopPropagation(); };
    const onDrop = async (e) => {
      prevent(e);
      if (!e.dataTransfer?.files?.length) return;
      const validated = validateFiles(e.dataTransfer.files);
      if (!validated.length) return;
      const staged = addLocalFiles(validated);
      await uploadFiles(staged);
    };
    el.addEventListener('dragenter', prevent);
    el.addEventListener('dragover', prevent);
    el.addEventListener('drop', onDrop);
    return () => {
      el.removeEventListener('dragenter', prevent);
      el.removeEventListener('dragover', prevent);
      el.removeEventListener('drop', onDrop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inline tag editing
  const startEditTags = (file) => {
    setEditingTagsId(file.id);
    setTagDrafts((d) => ({ ...d, [file.id]: (file.tags || []).map(normalizeTag) }));
    // seed query/options for this file context
    setTagQueryByFile((q) => ({ ...q, [file.id]: '' }));
    setOptionsByFile((o) => ({ ...o, [file.id]: [] }));
  };
  const cancelEditTags = () => setEditingTagsId(null);

  // Persist only tag IDs
  const saveEditTags = async (file) => {
    if (!assertPersisted(file, 'edit its tags')) return;
    const draftObjs = (tagDrafts[file.id] || []).map(normalizeTag);
    const ids = draftObjs.map(tagToId).filter(Boolean);
    try {
      await dataAPI(UPDATE, resource, { id: file.id, data: { tags: ids } }); // if your API expects 'tag_ids', change key here
      setFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, tags: draftObjs } : f)));
      enqueueSnackbar('Tags updated', { variant: 'success' });
      setEditingTagsId(null);
    } catch (e) {
      enqueueSnackbar('Failed to update tags', { variant: 'error' });
    }
  };

  // Inline knowledge base editing
  const startEditKB = (file) => {
    setEditingKBId(file.id);
    setKbDrafts((d) => ({ ...d, [file.id]: file.category || 'Uncategorized' }));
  };
  const cancelEditKB = () => setEditingKBId(null);
  const saveEditKB = async (file) => {
    if (!assertPersisted(file, 'change its knowledge base')) return;
    const kb = kbDrafts[file.id] || 'Uncategorized';
    try {
      try {
        await dataAPI(UPDATE, resource, { id: file.id, data: { category: kb } });
        setFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, category: kb } : f)));
        enqueueSnackbar('Knowledge base updated', { variant: 'success' });
      } catch (err) {
        enqueueSnackbar('Failed to update knowledge base: ' + (err?.message || err), { variant: 'error' });
      }
      setEditingKBId(null);
    } catch (e) {
      enqueueSnackbar('Failed to update knowledge base', { variant: 'error' });
    }
  };

  // Retry support for failed files
  const handleRetry = async (file) => {
    try {
      if (file.file) {
        setFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, status: 'Processing' } : f)));
        await uploadFiles([file]);
        return;
      }
      setFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, status: 'Processing' } : f)));
      let ok = false;
      try {
        const r1 = await dataAPI('RETRY', resource, { id: file.id });
        ok = !!r1;
      } catch {}
      if (!ok) {
        try {
          const r2 = await dataAPI('REPROCESS', resource, { id: file.id });
          ok = !!r2;
        } catch { }
      }
      if (!ok) {
        enqueueSnackbar('Retry not supported by server', { variant: 'warning' });
        setFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, status: 'Failed' } : f)));
      } else {
        enqueueSnackbar('Retry started', { variant: 'info' });
      }
    } catch (e) {
      enqueueSnackbar('Retry failed to start', { variant: 'error' });
      setFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, status: 'Failed' } : f)));
    }
  };

  // Drawer "Save"
  const saveEdit = async (file) => {
    if (!assertPersisted(file, 'save changes')) return;
    const tagObjs = (file.tags || []).map(normalizeTag);
    const tagIds = tagObjs.map(tagToId).filter(Boolean);
    const payload = {
      name: file.name,
      status: file.status,
      is_active: file.is_active === 'Yes',
      category: file.category || 'Uncategorized',
      tags: tagIds, // send IDs to backend
      ...(multiWidget ? { widget_ids: (file.widgets || []).map((w) => w.id) } : {}),
    };

    try {
      await dataAPI(UPDATE, resource, { id: file.id, data: payload });
      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id
            ? {
              ...f,
              name: payload.name,
              status: payload.status,
              is_active: payload.is_active ? 'Yes' : 'No',
              category: payload.category,
              // keep UI tags as objects (not ids)
              tags: tagObjs,
              widgets: file.widgets || [],
            }
            : f
        )
      );
      enqueueSnackbar('File updated successfully', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('Failed to update file', { variant: 'error' });
    }
  };

  // Create tag via API, return normalized {id,name}
  const createTag = async (name) => {
    const data = { category: TAG_CATEGORY_CODE, name };
    const res = await dataAPI(CREATE, TAGS_RESOURCE, { data });
    // support various shapes
    const raw = res?.data ?? res;
    const n = normalizeTag(raw?.data ?? raw);
    // ensure name at least
    return { id: n.id, name: n.name || name };
  };

  // Handle Enter-to-create/add for inline/file or drawer contexts
  const handleTagEnter = async (e, ctx) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.stopPropagation();
      return;
    }
    if (e.key !== 'Enter') return;

    const isDrawer = ctx === 'drawer';
    const queryRaw = isDrawer ? drawerTagQuery : (tagQueryByFile[ctx] || '');
    const q = (queryRaw || '').trim();
    if (!q) return;

    const currentObjs = (isDrawer ? (editingFile?.tags || []) : (tagDrafts[ctx] || [])).map(normalizeTag);
    const already = currentObjs.some(t => tagsEqual(t, { name: q }));
    if (already) {
      enqueueSnackbar(`"${q}" already added`, { variant: 'info' });
      return;
    }

    const optsRaw = isDrawer ? (drawerTagOptions || []) : (optionsByFile[ctx] || []);
    const matchFromOptions = optsRaw.find(o => tagsEqual(o, { name: q }));
    try {
      const toAddObj = matchFromOptions ? normalizeTag(matchFromOptions) : await createTag(q);
      const newArr = [...currentObjs, toAddObj];

      if (isDrawer) {
        setEditingFile((ef) => ({ ...ef, tags: newArr }));
        setDrawerTagQuery('');
      } else {
        setTagDrafts((d) => ({ ...d, [ctx]: newArr }));
        setTagQueryByFile((prev) => ({ ...prev, [ctx]: '' }));
      }
      if (!matchFromOptions) {
        enqueueSnackbar(`Tag "${toAddObj.name}" created`, { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Tag could not be created', { variant: 'error' });
    }
  };

  const columns = [
    { id: 'name', label: 'Name' },
    { id: 'is_active', label: 'Is Active' },
    { id: 'status', label: 'Status' },
    { id: 'category', label: 'Document Type' },
    { id: 'createdAt', label: 'Upload Date' },
  ];

  const allSelected = useMemo(() => files.length && Object.values(selection).filter(Boolean).length === files.length, [files, selection]);
  const toggleAll = (checked) => setSelection(checked ? Object.fromEntries(files.map((f) => [f.id, true])) : {});
  const toggleOne = (id) => setSelection((s) => ({ ...s, [id]: !s[id] }));

  // Badges shown next to the document name (shared between table and mobile cards).
  // The "Shared" badge is only meaningful when the operator can see the other
  // knowledge base — without the web widget it just reads as noise.
  const showSharedBadge = (file) => webWidgetAvailable && isSharedDoc(file);
  const renderDocBadges = (file) => (
    <>
      {showSharedBadge(file) && (
        <Tooltip
          arrow
          placement="right"
          title={`Shared with ${otherChannelLabel}. This is one document in both knowledge bases — edits made from either screen update both.`}
        >
          <Chip
            size="small"
            icon={<LinkIcon sx={{ fontSize: 14 }} />}
            label="Shared"
            sx={{ height: 22, cursor: 'help', fontWeight: 600 }}
            color="info"
            variant="outlined"
          />
        </Tooltip>
      )}
      {isAutoLessonsDoc(file.name) && (
        <Tooltip
          arrow
          placement="right"
          title={
            <Box sx={{ p: 0.5, maxWidth: 320 }}>
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <AutoAwesome fontSize="inherit" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Auto-generated lessons</Typography>
              </Box>
              <Typography variant="caption" component="div">
                Generated automatically by TourOptima. Each night it distills your team's edits to AI
                suggested replies into reusable lessons for the AI. It is created <b>inactive</b> and is
                <b> not used by the AI</b> until you review and switch it on. Auto-generated content can be
                imperfect &mdash; please review it before activating.
              </Typography>
            </Box>
          }
        >
          <Chip
            size="small"
            icon={<AutoAwesome sx={{ fontSize: 14 }} />}
            label="Auto-generated"
            sx={{
              height: 22,
              cursor: 'help',
              fontWeight: 600,
              color: 'primary.main',
              borderColor: 'primary.main',
              bgcolor: 'transparent',
              '& .MuiChip-icon': { color: '#f5a623', ml: '4px' },
            }}
            variant="outlined"
          />
        </Tooltip>
      )}
      {isStaffAssertionsDoc(file.name) && (
        <Tooltip
          arrow
          placement="right"
          title={
            <Box sx={{ p: 0.5, maxWidth: 320 }}>
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <FactCheckOutlined fontSize="inherit" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>For review only</Typography>
              </Box>
              <Typography variant="caption" component="div">{STAFF_ASSERTIONS_EXPLAINER}</Typography>
            </Box>
          }
        >
          <Chip
            size="small"
            icon={<FactCheckOutlined sx={{ fontSize: 14 }} />}
            label="For review only"
            sx={{ height: 22, cursor: 'help', fontWeight: 600, color: 'text.secondary', '& .MuiChip-icon': { ml: '4px' } }}
            variant="outlined"
          />
        </Tooltip>
      )}
    </>
  );

  // The backlog worksheet is never read by the AI, so its Yes/No would be meaningless.
  const renderIsActive = (file) => (isStaffAssertionsDoc(file.name)
    ? <Typography variant="body2" color="text.secondary">Not used by AI</Typography>
    : file.is_active);

  // Column sort, then the review-only worksheet(s) last so they sit below the real training docs.
  const sortRows = (rows) => {
    const sorted = stableSort(rows, getComparator(order, orderBy));
    return [
      ...sorted.filter((f) => !isStaffAssertionsDoc(f.name)),
      ...sorted.filter((f) => isStaffAssertionsDoc(f.name)),
    ];
  };

  // Per-row action buttons (shared between table and mobile cards), in a fixed order;
  // null where the action doesn't apply to this row.
  const rowActions = (file) => [
    ['preview', <IconButton onClick={() => handlePreview(file)} size="small"><Visibility fontSize="small" /></IconButton>],
    ['edit', fileHasWrite(file) && (
      <IconButton onClick={() => { setEditingFile(file); setEditingFileBaseline(editFileSnapshot(file)); setDrawerOpen(true); }} size="small">
        <Edit fontSize="small" />
      </IconButton>
    )],
    // `link` is the attachment URL, so it's the marker for a file-backed doc —
    // pasted-text and link docs have nothing to replace.
    ['replace', fileHasWrite(file) && !file.file && !!file.link && (
      <Tooltip title="Replace file (upload a new version)">
        <IconButton onClick={() => handleReplaceClick(file)} size="small"><UploadFile fontSize="small" /></IconButton>
      </Tooltip>
    )],
    // Copy makes an independent fork, so it's meaningless for a doc already living
    // in both KBs; those get Unshare instead.
    ['copy', webWidgetAvailable && canCopyToOtherChannel && !isSharedDoc(file) && (
      <Tooltip title={`Copy to ${otherChannelLabel} (independent copy)`}>
        <IconButton onClick={() => handleCopyToOtherChannel(file)} size="small"><ContentCopy fontSize="small" /></IconButton>
      </Tooltip>
    )],
    ['share', webWidgetAvailable && canShareAcrossChannels && !file.file && (
      <Tooltip
        title={isSharedDoc(file)
          ? `Unshare (keep only in ${KB_CHANNEL_LABELS[channel]})`
          : `Share with ${otherChannelLabel} (one document in both — edits update both)`}
      >
        <IconButton onClick={() => handleShareToggle(file)} size="small">
          {isSharedDoc(file) ? <LinkOff fontSize="small" /> : <LinkIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    )],
    // CP-5545: always in its slot, disabled (grey) where this user may not delete the file.
    ['delete', <DeleteIconButton onClick={() => handleDelete(file)} allowed={fileHasDelete(file)} />],
  ];

  // In the table every row reserves the same slots (any action some listed row shows),
  // leaving a blank where an action doesn't apply, so the icons line up in columns and
  // Delete never drifts under another row's Replace. Mobile cards pass no slots and
  // just show what applies.
  const actionSlots = new Set(filteredFiles.flatMap((f) => rowActions(f).filter(([, node]) => node).map(([key]) => key)));
  const renderRowActions = (file, slots) => rowActions(file)
    .filter(([key, node]) => (slots ? slots.has(key) : node))
    .map(([key, node]) => (
      <React.Fragment key={key}>
        {node || (
          <IconButton size="small" disabled tabIndex={-1} aria-hidden="true" data-testid="kb-action-blank" sx={{ visibility: 'hidden' }}>
            <Visibility fontSize="small" />
          </IconButton>
        )}
      </React.Fragment>
    ));

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: '12px' }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5} flexWrap="wrap" gap={1}>
          <Box sx={{ flex: '0 1 auto', minWidth: 0 }}>
            <Typography variant="h6">Training Materials Upload & Management</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {channel === KB_CHANNEL_WIDGET
                ? 'Documents for the public web chat widget (sales assistant) — a separate knowledge base from the support one.'
                : (webWidgetAvailable
                    ? 'Documents for the support auto-reply / suggested-reply assistant — a separate knowledge base from the web widget one.'
                    : 'Train your chatbot with PDFs and documents. For best results on FAQs, upload a file with questions and answers.')}
            </Typography>
            {showTrainingInboxNote && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Your chatbot also uses the answers your team trained in the{' '}
                <Link component="button" variant="body2" onClick={onOpenTrainingInbox} underline="hover" sx={{ verticalAlign: 'baseline' }}>
                  AI training inbox
                </Link>
                . Manage them there.
              </Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1.7} sx={{ flexWrap: 'wrap' }}>
            <IconButton onClick={() => fetchFiles({ force: true, showSpinner: true })} title="Refresh training files" sx={{ mr: -1.3 }}>
              <ReloadIcon />
            </IconButton>
            {!isMobile && (
              <DeleteButton onClick={handleBulkDelete} allowed={hasDelete} disabled={!Object.values(selection).some(Boolean)}>Delete Selected</DeleteButton>
            )}
            <Button variant="contained" startIcon={<Upload />} onClick={handleFileUploadClick} disabled={uploading || !hasWrite}>
              {uploading ? 'Uploading…' : 'Upload Training File'}
            </Button>
            <input ref={fileInputRef} type="file" multiple hidden accept={ACCEPTED_EXT.join(',')} onChange={onFileInputChange} />
            <input ref={replaceInputRef} type="file" hidden accept={ACCEPTED_EXT.join(',')} onChange={onReplaceInputChange} />
          </Box>
        </Box>

        <Box mb={2} sx={{ maxWidth: 600 }}>
          <TextField
            variant="filled"
            size="small"
            placeholder="Search training files..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ width: '100%' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              sx: {
                '& input': {
                  py: '10px',
                },
              },
            }}
          />
        </Box>

      <Paper ref={dropRef} variant="outlined" sx={{ p: 2, mb: 2, textAlign: 'center', borderStyle: 'dashed' }}>
        {hasWrite ? 'Drag & drop files here to upload' : 'View-only: uploads disabled'}
      </Paper>

      <Box sx={{ position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {isMobile ? (
        // Mobile card view
        <Box>
          {sortRows(filteredFiles).map((file) => (
            <Card key={file.id} variant="outlined" sx={{ mb: 1.5 }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                    {file.name}
                  </Typography>
                  {(showSharedBadge(file) || isAutoLessonsDoc(file.name) || isStaffAssertionsDoc(file.name)) && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {renderDocBadges(file)}
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96, flexShrink: 0 }}>
                    Is Active
                  </Typography>
                  <Typography variant="body2" component="div">{renderIsActive(file)}</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96, flexShrink: 0 }}>
                    Status
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getStatusChip(file.status)}
                    {file.status === 'Failed' && (
                      <Button size="small" variant="text" onClick={() => handleRetry(file)} sx={{ ml: 1 }}>Retry</Button>
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96, flexShrink: 0 }}>
                    Document Type
                  </Typography>
                  <Typography variant="body2">{file.category || 'Uncategorized'}</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96, flexShrink: 0 }}>
                    Upload Date
                  </Typography>
                  <Typography variant="body2">{file.created_ts_display}</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96, flexShrink: 0 }}>
                    Tags
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
                    {(file.tags || []).length > 0
                      ? file.tags.map((tag, idx) => {
                          const { name, id } = normalizeTag(tag);
                          return <Chip key={`${id ?? name}-${idx}`} label={name} size="small" />;
                        })
                      : <Typography variant="caption" color="text.secondary">No tags</Typography>}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', mt: 0.5 }}>
                  {renderRowActions(file)}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
        ) : (
        <TableContainer component={Paper}>
          <Table
            sx={{
              '& .MuiTableRow-root': {
                height: 'auto',
              },
              '& .MuiTableCell-root': {
                paddingTop: '8px',
                paddingBottom: '8px',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <input type="checkbox" checked={!!allSelected} onChange={(e) => toggleAll(e.target.checked)} disabled={!hasDelete}/>
                </TableCell>
                {columns.map((col) => (
                  <TableCell key={col.id} sortDirection={orderBy === col.id ? order : false}>
                    <TableSortLabel active={orderBy === col.id} direction={orderBy === col.id ? order : 'asc'} onClick={() => handleRequestSort(col.id)}>
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell>Tags</TableCell>
                <TableCell align="center" sx={{ minWidth: webWidgetAvailable ? 160 : 120 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortRows(filteredFiles)
                .map((file) => (
                  <TableRow key={file.id} hover>
                    <TableCell padding="checkbox">
                      <input type="checkbox" checked={!!selection[file.id]} onChange={() => toggleOne(file.id)} disabled={!hasDelete} />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <span>{file.name}</span>
                        {renderDocBadges(file)}
                      </Box>
                    </TableCell>
                    <TableCell>{renderIsActive(file)}</TableCell>
                    <TableCell>
                      {getStatusChip(file.status)}
                      {file.status === 'Failed' && (
                        <Button size="small" variant="text" onClick={() => handleRetry(file)} sx={{ ml: 1 }}>Retry</Button>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 260 }}>
                      {editingKBId === file.id ? (
                        <Box display="flex" gap={1} alignItems="center">
                          <Autocomplete
                            disabled={!hasWrite}
                            size="small"
                            freeSolo
                            options={kbOptions}
                            value={kbDrafts[file.id] || ''}
                            onChange={(e, v) => setKbDrafts((d) => ({ ...d, [file.id]: v || '' }))}
                            renderInput={(params) => <TextField {...params} label="Document Type" placeholder="Select or type" />}
                            sx={{ flex: 1 }}
                          />
                          <IconButton size="small" onClick={cancelEditKB} aria-label="Cancel KB"><Close fontSize="small"/></IconButton>
                          <IconButton size="small" onClick={() => saveEditKB(file)} aria-label="Save KB"><Save fontSize="small"/></IconButton>
                        </Box>
                      ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2">{file.category || 'Uncategorized'}</Typography>
                          {fileHasWrite(file) && (
                            <IconButton size="small" onClick={() => startEditKB(file)} aria-label="Edit document type"><Edit fontSize="small"/></IconButton>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{file.created_ts_display}</TableCell>
                    <TableCell sx={{ minWidth: 320 }}>
                      {editingTagsId === file.id ? (
                        <Box display="flex" gap={1} alignItems="center" sx={{ width: '100%' }}>
                          <RemoteAutocomplete
                            multiple
                            resource={TAGS_RESOURCE}
                            apiFilters={[{ field: 'category_code', op: 'equals', value: TAG_CATEGORY_CODE }]}
                            value={(tagDrafts[file.id] || []).map(normalizeTag)}
                            onChange={(e, v) => setTagDrafts((d) => ({ ...d, [file.id]: (v || []).map(normalizeTag) }))}
                            disabled={!hasWrite}
                            setOptionsCount={() => {}}
                            setOptions={(opts) => setOptionsByFile((o) => ({ ...o, [file.id]: opts }))}
                            fullWidth
                            size="small"
                            label="Tags"
                            variant="filled"
                            clearOnBlur={false}
                            noOptionsText={(tagQueryByFile[file.id] || '').trim()
                              ? `Press Enter to add "${(tagQueryByFile[file.id] || '').trim()}"`
                              : "Type to search"}
                            renderOption={renderBoldTagOption}
                            onQueryChange={(q) => setTagQueryByFile((prev) => ({ ...prev, [file.id]: q }))}
                            inputValue={tagQueryByFile[file.id] || ''}
                            onInputChange={(e, value) =>
                              setTagQueryByFile(prev => ({ ...prev, [file.id]: value }))
                            }
                            onKeyDown={(e) => handleTagEnter(e, file.id)}
                            getOptionLabel={(opt) => tagToName(opt)}
                            isOptionEqualToValue={(opt, val) => tagsEqual(opt, val)}
                            renderTags={(value, getTagProps) =>
                              value.map((opt, i) => (
                                <Chip {...getTagProps({ index: i })} size="small" label={tagToName(opt)} />
                              ))
                            }
                          />
                          <IconButton size="small" onClick={cancelEditTags} aria-label="Cancel tags"><Close fontSize="small"/></IconButton>
                          <IconButton size="small" onClick={() => saveEditTags(file)} aria-label="Save tags"><Save fontSize="small" /></IconButton>
                        </Box>
                      ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box>
                            {(file.tags || []).map((tag, idx) => {
                              const { name, id } = normalizeTag(tag);
                              return <Chip key={`${id ?? name}-${idx}`} label={name} size="small" sx={{ mr: 0.5, mb: 0.5 }} />;
                            })}
                            {(!file.tags || file.tags.length === 0) && <Typography variant="caption" color="text.secondary">No tags</Typography>}
                          </Box>
                          {fileHasWrite(file) && (
                            <IconButton size="small" onClick={() => startEditTags(file)} aria-label="Edit tags"><Edit fontSize="small"/></IconButton>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ minWidth: webWidgetAvailable ? 160 : 120, whiteSpace: 'nowrap' }}>
                      {renderRowActions(file, actionSlots)}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </Box>

      {!loading && filteredFiles.length === 0 && (
        <Box textAlign="center" py={6} color="text.secondary">
          <Typography>No files match your search.</Typography>
        </Box>
      )}
      </Paper>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box role="presentation" sx={{ p: 3, width: { xs: '100vw', sm: 360 }, maxWidth: 360 }}>
          <Typography variant="h6">Edit File</Typography>
          <TextField
            label="Name"
            value={editingFile?.name || ''}
            onChange={(e) => setEditingFile({ ...editingFile, name: e.target.value })}
            fullWidth margin="normal"
            disabled={!hasWrite}
          />

          {isStaffAssertionsDoc(editingFile?.name) ? (
            <Alert severity="info" icon={<FactCheckOutlined fontSize="inherit" />} sx={{ mt: 2 }}>
              {STAFF_ASSERTIONS_EXPLAINER}
            </Alert>
          ) : (
            <Box display="flex" alignItems="center" mt={2}>
              <Switch
                checked={editingFile?.is_active === 'Yes'}
                onChange={(e) => setEditingFile({ ...editingFile, is_active: e.target.checked ? 'Yes' : 'No' })}
                disabled={!hasWrite}
              />
              <Typography>Is Active</Typography>
            </Box>
          )}

          <Autocomplete
            disabled={!hasWrite}
            size="small"
            freeSolo
            options={kbOptions || []}
            value={editingFile?.category || ''}
            onChange={(e, v) => setEditingFile({ ...editingFile, category: v || '' })}
            renderInput={(params) => (
              <TextField {...params} label="Document Type" placeholder="Select or type" margin="normal" />
            )}
          />

          <RemoteAutocomplete
            disabled={!hasWrite}
            multiple
            resource={TAGS_RESOURCE}
            apiFilters={[{ field: 'category_code', op: 'equals', value: TAG_CATEGORY_CODE }]}
            value={(editingFile?.tags || []).map(normalizeTag)}
            onChange={(e, v) => setEditingFile({ ...editingFile, tags: (v || []).map(normalizeTag) })}
            setOptionsCount={() => {}}
            setOptions={(opts) => setDrawerTagOptions((opts || []).map(normalizeTag))}
            fullWidth
            size="small"
            label="Tags"
            variant="filled"
            clearOnBlur={false}
            noOptionsText={drawerTagQuery.trim() ? `Press Enter to add "${drawerTagQuery.trim()}"` : 'Type to search'}
            renderOption={renderBoldTagOption}
            onQueryChange={setDrawerTagQuery}
            inputValue={drawerTagQuery}
            onInputChange={(e, value) => setDrawerTagQuery(value)}
            onKeyDown={(e) => handleTagEnter(e, 'drawer')}
            getOptionLabel={(opt) => tagToName(opt)}
            isOptionEqualToValue={(opt, val) => tagsEqual(opt, val)}
            renderTags={(value, getTagProps) =>
              value.map((opt, i) => (
                <Chip {...getTagProps({ index: i })} size="small" label={tagToName(opt)} />
              ))
            }
          />

          {multiWidget && (
            <WidgetsPicker
              widgets={widgets}
              disabled={!hasWrite}
              value={editingFile?.widgets || []}
              onChange={(v) => setEditingFile({ ...editingFile, widgets: v })}
              sx={{ mt: 2 }}
            />
          )}

          <FormActionButtons
            mode="edit"
            size="medium"
            sx={{ mt: 3 }}
            onCancel={() => setDrawerOpen(false)}
            onSubmit={() => { saveEdit(editingFile); setDrawerOpen(false); }}
            submitDisabled={!hasWrite || !editingFileChanged}
          />
        </Box>
      </Drawer>

      {/* A shared document is one row feeding both KBs, so the delete dialog offers
          removing it from this screen's KB only, next to deleting it everywhere.
          Without a widget there is no second KB to keep it in. */}
      {fileToDelete && webWidgetAvailable && isSharedDoc(fileToDelete) && (
        <Dialog open onClose={cancelDelete} maxWidth="sm" fullWidth>
          <DialogTitle>Delete shared document</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              “{fileToDelete.name}” is shared between the {KB_CHANNEL_LABELS[channel]} and
              the {otherChannelLabel}.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button onClick={cancelDelete}>Cancel</Button>
            {canShareAcrossChannels && (
              <Button variant="outlined" onClick={removeFromThisChannelOnly}>
                Remove from {KB_CHANNEL_LABELS[channel]} only
              </Button>
            )}
            <DeleteButton onClick={confirmDelete}>
              Delete from both {KB_CHANNEL_LABELS[channel]} and {otherChannelLabel}
            </DeleteButton>
          </DialogActions>
        </Dialog>
      )}
      {fileToDelete && !(webWidgetAvailable && isSharedDoc(fileToDelete)) && (
        <ConfirmationDialog
          open={!!fileToDelete}
          onCancel={cancelDelete}
          onConfirm={confirmDelete}
          title="Please confirm"
          okButtonText="Delete"
          cancelButtonText="Cancel"
        >
          Are you sure you wish to delete "{fileToDelete.name}"?
        </ConfirmationDialog>
      )}

      {pendingReplacement && (
        <ConfirmationDialog
          open={!!pendingReplacement}
          onCancel={() => setPendingReplacement(null)}
          onConfirm={confirmReplacement}
          title="Different file type"
          okButtonText="Replace and rename"
          cancelButtonText="Cancel"
        >
          “{pendingReplacement.file.name}” will be replaced by “{pendingReplacement.incoming.name}”,
          which is a different file type. The document will be renamed to match so it is read correctly.
        </ConfirmationDialog>
      )}

      {/* Word documents can't be written back as .docx, so the first save
          converts the document and needs a decision about the original. */}
      <Dialog open={convertPrompt.open} onClose={() => setConvertPrompt({ open: false, keepOriginal: true })} maxWidth="sm" fullWidth>
        <DialogTitle>Save as editable text</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            “{preview.name}” will be saved as a Markdown document so it can be edited here.
            The AI already ignores Word formatting, so nothing it uses is lost.
          </Typography>
          <RadioGroup
            value={convertPrompt.keepOriginal ? 'keep' : 'replace'}
            onChange={(e) => setConvertPrompt((c) => ({ ...c, keepOriginal: e.target.value === 'keep' }))}
          >
            <FormControlLabel
              value="keep"
              control={<Radio />}
              label="Keep the original Word file as an inactive archive (not used by the AI)"
            />
            <FormControlLabel
              value="replace"
              control={<Radio />}
              label="Replace the original Word file"
            />
          </RadioGroup>
          {!convertPrompt.keepOriginal && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              The original Word file will be deleted and cannot be recovered.
              {preview.file?.link && (
                <>
                  {' '}
                  <a href={preview.file.link} target="_blank" rel="noopener noreferrer">Download it first</a>
                  {' '}if you need a copy.
                </>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertPrompt({ open: false, keepOriginal: true })}>Cancel</Button>
          <Button onClick={confirmConvert} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={preview.open} onClose={closePreview} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pr: 1 }}>
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {preview.name}{preview.editing ? ' — Editing' : ''}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {!preview.editing && preview.isMarkdown && !preview.loading && !preview.error && (
              <Button size="small" onClick={() => setPreview((p) => ({ ...p, showRendered: !p.showRendered }))}>
                {preview.showRendered ? 'View source' : 'View rendered'}
              </Button>
            )}
            {/* Word docs are shown converted, so keep a route back to the file
                that was actually uploaded. */}
            {!preview.editing && preview.convertTo && preview.file?.link && (
              <Button
                size="small"
                startIcon={<Download fontSize="small" />}
                href={preview.file.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                Original
              </Button>
            )}
            {preview.editing && preview.rich && (
              <Button size="small" onClick={toggleWysiwyg}>
                {preview.wysiwyg ? 'Edit source' : 'Formatted editor'}
              </Button>
            )}
            {!preview.editing && preview.editable && (
              <Button size="small" startIcon={<Edit fontSize="small" />} onClick={startEditPreview}>Edit</Button>
            )}
            <IconButton onClick={closePreview} size="small" aria-label="Close preview"><Close fontSize="small" /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {isStaffAssertionsDoc(preview.name) && (
            <Alert severity="info" icon={<FactCheckOutlined fontSize="inherit" />} sx={{ mb: 2 }}>
              <b>For review only.</b> {STAFF_ASSERTIONS_EXPLAINER}
            </Alert>
          )}
          {preview.warning && !preview.loading && !preview.error && (
            <Alert severity="info" sx={{ mb: 2 }}>{preview.warning}</Alert>
          )}
          {preview.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : preview.error ? (
            <Typography color="error">{preview.error}</Typography>
          ) : preview.editing && preview.wysiwyg ? (
            <QuillEditor
              value={preview.quillHtml}
              onChange={(html) => setPreview((p) => ({ ...p, draft: htmlToMarkdown(html) }))}
              modules={KB_QUILL_MODULES}
              formats={KB_QUILL_FORMATS}
              preserveParagraphs
              hideButtonBar
              containerHeight="380px"
            />
          ) : preview.editing ? (
            <TextField
              value={preview.draft}
              onChange={(e) => setPreview((p) => ({ ...p, draft: e.target.value }))}
              multiline
              minRows={16}
              fullWidth
              variant="outlined"
              spellCheck={false}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13, alignItems: 'flex-start' } }}
            />
          ) : preview.isMarkdown && preview.showRendered ? (
            <Box
              sx={{ '& img': { maxWidth: '100%' }, '& pre': { overflowX: 'auto', p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }, '& table': { borderCollapse: 'collapse' }, '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1, py: 0.5 } }}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(preview.content) }}
            />
          ) : (
            <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: 13 }}>
              {preview.content}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {preview.editing ? (
            // A Word doc always has something to save even when untouched:
            // the conversion itself is the change.
            <FormActionButtons
              mode="edit"
              size="medium"
              onCancel={cancelEditPreview}
              cancelDisabled={preview.saving}
              onSubmit={requestSave}
              submitDisabled={preview.saving || (!preview.convertTo && preview.draft === preview.content)}
              submitIcon={preview.saving ? <CircularProgress size={16} /> : <Save fontSize="small" />}
            />
          ) : (
            <Button onClick={closePreview}>Close</Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
